import { useEffect, useMemo, useState } from "react";

import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema.ts";

import type { QuoteBoardStatusContext } from "../utils/quote-board-status.ts";
import {
	canTenderQuoteBoardRow,
	isQuoteBoardSelectionEligible,
} from "../utils/quote-board-status.ts";

type BoardItem = QuoteBoardItem | CustomerQuoteBoardItem;

export const useBulkTenderSelection = (
	items: BoardItem[],
	context: QuoteBoardStatusContext = {},
) => {
	const { isInternal = false } = context;
	const [selectedQuoteIds, setSelectedQuoteIds] = useState<Set<number>>(
		() => new Set(),
	);

	const eligibleQuoteIds = useMemo(
		() =>
			items
				.filter((item) => isQuoteBoardSelectionEligible(item, { isInternal }))
				.map((item) => item.quoteId),
		[items, isInternal],
	);
	const eligibleQuoteIdSet = useMemo(
		() => new Set(eligibleQuoteIds),
		[eligibleQuoteIds],
	);

	useEffect(() => {
		setSelectedQuoteIds((current) => {
			const next = new Set(
				[...current].filter((quoteId) => eligibleQuoteIdSet.has(quoteId)),
			);
			if (
				next.size === current.size &&
				[...next].every((quoteId) => current.has(quoteId))
			) {
				return current;
			}
			return next;
		});
	}, [eligibleQuoteIdSet]);

	const toggleQuote = (quoteId: number, selected: boolean) => {
		if (!eligibleQuoteIdSet.has(quoteId)) return;
		setSelectedQuoteIds((current) => {
			const next = new Set(current);
			if (selected) next.add(quoteId);
			else next.delete(quoteId);
			return next;
		});
	};

	const toggleBatch = (
		bulkQuoteId: string,
		selected: boolean,
		visibleQuoteIds: readonly number[] = eligibleQuoteIds,
	) => {
		const visibleQuoteIdSet = new Set(visibleQuoteIds);
		const batchQuoteIds = items
			.filter(
				(item) =>
					item.bulkQuoteId === bulkQuoteId &&
					visibleQuoteIdSet.has(item.quoteId) &&
					canTenderQuoteBoardRow(item, { isInternal }),
			)
			.map((item) => item.quoteId);
		setSelectedQuoteIds((current) => {
			const next = new Set(current);
			for (const quoteId of batchQuoteIds) {
				if (selected) next.add(quoteId);
				else next.delete(quoteId);
			}
			return next;
		});
	};

	const clearSelection = () => setSelectedQuoteIds(new Set());

	return {
		selectedQuoteIds,
		selectedCount: selectedQuoteIds.size,
		toggleQuote,
		toggleBatch,
		clearSelection,
	};
};
